const CHECK_IN_TIME = { hours: 16, minutes: 0 }
const CHECK_OUT_TIME = { hours: 10, minutes: 0 }
const BOOKING_NUMBER_PATTERN = 'BK-{YYYYMMDD}'

export const BOOKING_STATUS_REQUEST = 'angefragt'

export const BOOKING_STATUS_CONFIRMED = [
  'bestätigt',
  'bestaetigt',
  'confirmed',
  'bezahlt',
  'paid',
  'eigenbelegung'
]

export const BOOKING_STATUS_PENDING = [
  'angefragt',
  'requested',
  'pending',
  'anfrage'
]

export const BOOKING_STATUS_CANCELLED = [
  'storniert',
  'storno',
  'stornierung',
  'cancelled',
  'canceled',
  'cancel',
  'annulliert',
  'annullation',
  'abgesagt'
]

export const normalizeBookingStatus = (status) => {
  if (status == null) return ''
  return String(status)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export const categorizeBookingStatus = (status) => {
  const normalized = normalizeBookingStatus(status)
  if (!normalized) return 'confirmed'

  if (BOOKING_STATUS_CANCELLED.some((value) => normalized === value || normalized.includes(value))) {
    return 'cancelled'
  }

  if (BOOKING_STATUS_PENDING.some((value) => normalized === value || normalized.includes(value))) {
    return 'pending'
  }

  if (BOOKING_STATUS_CONFIRMED.some((value) => normalized === value || normalized.includes(value))) {
    return 'confirmed'
  }

  return 'confirmed'
}

export const isBlockingBookingStatus = (status) => categorizeBookingStatus(status) === 'confirmed'

export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') return ''
  return email.trim().toLowerCase()
}

export const generateBookingNumber = (pattern = BOOKING_NUMBER_PATTERN) => {
  const now = new Date()
  const pad = (num, length = 2) => String(num).padStart(length, '0')

  return pattern.replace(/\{([^}]+)\}/g, (match, placeholder) => {
    switch (placeholder) {
      case 'YYYY':
        return now.getFullYear().toString()
      case 'MM':
        return pad(now.getMonth() + 1)
      case 'DD':
        return pad(now.getDate())
      case 'YYYYMMDD':
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
      default:
        return match
    }
  })
}

export const parseDateInput = (value) => {
  if (!value) return null
  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

export const buildCheckInDateTime = (dateInput) => {
  const date = parseDateInput(dateInput)
  if (!date) return null
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    CHECK_IN_TIME.hours,
    CHECK_IN_TIME.minutes,
    0,
    0
  )
}

export const buildCheckOutDateTime = (dateInput) => {
  const date = parseDateInput(dateInput)
  if (!date) return null
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    CHECK_OUT_TIME.hours,
    CHECK_OUT_TIME.minutes,
    0,
    0
  )
}

export const validateBookingRequest = (payload, apartment = null) => {
  const errors = []

  if (!payload.first_name?.trim()) errors.push('Bitte geben Sie Ihren Vornamen an.')
  if (!payload.last_name?.trim()) errors.push('Bitte geben Sie Ihren Nachnamen an.')
  if (!normalizeEmail(payload.email)) errors.push('Bitte geben Sie eine gültige E-Mail-Adresse an.')
  if (!payload.phone?.trim()) errors.push('Bitte geben Sie Ihre Telefonnummer an.')

  const checkIn = parseDateInput(payload.check_in)
  const checkOut = parseDateInput(payload.check_out)

  if (!checkIn) errors.push('Bitte wählen Sie ein Anreisedatum.')
  if (!checkOut) errors.push('Bitte wählen Sie ein Abreisedatum.')
  if (checkIn && checkOut && checkOut <= checkIn) {
    errors.push('Das Abreisedatum muss nach dem Anreisedatum liegen.')
  }

  const guestCount = Number(payload.guest_count)
  if (!Number.isFinite(guestCount) || guestCount < 1) {
    errors.push('Bitte geben Sie die Anzahl der Personen an.')
  }

  const maxGuests = Number(apartment?.max_guests)
  if (maxGuests > 0 && guestCount > maxGuests) {
    errors.push(`Maximal ${maxGuests} Personen in dieser Ferienwohnung.`)
  }

  return errors
}

export const buildGuestPayload = (payload) => ({
  first_name: payload.first_name.trim(),
  last_name: payload.last_name.trim(),
  email: normalizeEmail(payload.email),
  phone: payload.phone.trim(),
  ...(payload.street?.trim() ? { street: payload.street.trim() } : {}),
  ...(payload.postal_code?.trim() ? { postal_code: payload.postal_code.trim() } : {}),
  ...(payload.city?.trim() ? { city: payload.city.trim() } : {}),
  ...(payload.country?.trim() ? { country: payload.country.trim() } : { country: 'Deutschland' })
})

export const buildGuestUpdatePayload = (existingGuest, payload) => {
  const next = buildGuestPayload(payload)
  const updates = {}

  Object.entries(next).forEach(([key, value]) => {
    const current = existingGuest?.[key]
    if (value !== undefined && value !== '' && current !== value) {
      updates[key] = value
    }
  })

  return updates
}

export const buildBookingPayload = ({ apartmentId, guestId, payload }) => {
  const checkIn = buildCheckInDateTime(payload.check_in)
  const checkOut = buildCheckOutDateTime(payload.check_out)

  return {
    vacation_rental_id: apartmentId,
    guest_id: guestId,
    booking_number: generateBookingNumber(),
    check_in: checkIn.toISOString(),
    check_out: checkOut.toISOString(),
    guest_count: Number(payload.guest_count),
    status: BOOKING_STATUS_REQUEST,
    ...(payload.notes?.trim() ? { notes: payload.notes.trim() } : {})
  }
}

export const getApartmentId = (apartment) =>
  apartment?._id || apartment?.id || apartment?.vacation_rental_id || null
